import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Stethoscope, Building2, Sparkles, ShieldCheck, MapPin, MessageSquare } from "lucide-react";

export default function Landing() {
    return (
        <div className="min-h-screen bg-[#F9F9F7]" data-testid="landing-page">
            {/* NAV */}
            <header className="sticky top-0 z-40 border-b border-[#E5E5E0] bg-white/95 backdrop-blur">
                <div className="mx-auto max-w-7xl px-6 h-16 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
                        <div className="h-8 w-8 rounded-md bg-[#1C3F39] grid place-items-center text-white font-display font-bold">H</div>
                        <span className="font-display text-lg font-bold tracking-tight">HealthLink</span>
                    </Link>
                    <nav className="hidden md:flex items-center gap-8 text-sm">
                        <a href="#how" className="hover:text-[#1C3F39] transition-colors">Come funziona</a>
                        <a href="#pro" className="hover:text-[#1C3F39] transition-colors">Per Professionisti</a>
                        <a href="#facility" className="hover:text-[#1C3F39] transition-colors">Per Strutture</a>
                    </nav>
                    <div className="flex items-center gap-2">
                        <Link to="/login">
                            <Button variant="ghost" className="text-sm" data-testid="nav-login-btn">Accedi</Button>
                        </Link>
                        <Link to="/register">
                            <Button className="bg-[#1C3F39] hover:bg-[#2A5C53] text-sm" data-testid="nav-register-btn">Registrati</Button>
                        </Link>
                    </div>
                </div>
            </header>

            {/* HERO */}
            <section className="hl-grain">
                <div className="mx-auto max-w-7xl px-6 pt-20 pb-24 grid md:grid-cols-12 gap-10 items-center">
                    <div className="md:col-span-7">
                        <span className="hl-pill" data-testid="hero-tagline">
                            <Sparkles className="w-3.5 h-3.5" /> Marketplace sanitario italiano
                        </span>
                        <h1 className="mt-6 font-display text-5xl md:text-6xl font-bold tracking-tight leading-[1.05]">
                            Copri un turno,<br/>trova un professionista.<br/>
                            <span className="text-[#1C3F39]">In poche ore, non giorni.</span>
                        </h1>
                        <p className="mt-6 max-w-xl text-lg text-[#5C5C5C]">
                            HealthLink connette professionisti sanitari con Partita IVA e strutture che hanno bisogno di sostituzioni, turni scoperti o collaborazioni temporanee.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link to="/register?role=professional">
                                <Button size="lg" className="bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="hero-cta-professional">
                                    <Stethoscope className="w-4 h-4 mr-2" /> Sono un professionista
                                </Button>
                            </Link>
                            <Link to="/register?role=facility">
                                <Button size="lg" variant="outline" className="border-[#1C3F39] text-[#1C3F39] hover:bg-[#1C3F39] hover:text-white" data-testid="hero-cta-facility">
                                    <Building2 className="w-4 h-4 mr-2" /> Sono una struttura
                                </Button>
                            </Link>
                        </div>
                        <div className="mt-10 flex items-center gap-6 text-xs text-[#5C5C5C]">
                            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1C3F39]" /> GDPR compliant</span>
                            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1C3F39]" /> Profili verificati</span>
                            <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#1C3F39]" /> Base gratuita</span>
                        </div>
                    </div>
                    <div className="md:col-span-5">
                        <div className="hl-card p-1 overflow-hidden">
                            <img
                                src="https://images.unsplash.com/photo-1758691462878-6edc3d3da1be?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBoZWFsdGhjYXJlJTIwcHJvZmVzc2lvbmFsJTIwY2xpbmljfGVufDB8fHx8MTc4NTg1ODUyOHww&ixlib=rb-4.1.0&q=85"
                                alt="Professionista sanitario al lavoro"
                                className="w-full h-[420px] object-cover rounded-xl"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW */}
            <section id="how" className="border-t border-[#E5E5E0] bg-white">
                <div className="mx-auto max-w-7xl px-6 py-20">
                    <div className="flex items-end justify-between mb-12">
                        <h2 className="font-display text-4xl font-bold tracking-tight max-w-xl">Un flusso semplice, un match professionale.</h2>
                        <span className="text-sm text-[#5C5C5C] hidden md:block">Progettato per il settore sanitario italiano</span>
                    </div>
                    <div className="grid md:grid-cols-12 gap-6">
                        {[
                            { i: MapPin, t: "Profilo & Zona", d: "Il professionista indica professione, esperienza, zona e disponibilità." },
                            { i: Building2, t: "Annuncio", d: "La struttura pubblica il turno con data, orario e compenso lordo." },
                            { i: Sparkles, t: "Match intelligente", d: "Algoritmo pesato su professione, distanza, disponibilità ed esperienza." },
                            { i: MessageSquare, t: "Chat & Recensione", d: "Dopo la candidatura si comunica direttamente; a fine turno arriva la recensione." },
                        ].map((f, idx) => (
                            <div key={idx} className="hl-card p-6 md:col-span-3" data-testid={`how-step-${idx}`}>
                                <div className="w-10 h-10 rounded-md bg-[#EEF3F1] text-[#1C3F39] grid place-items-center">
                                    <f.i className="w-5 h-5" />
                                </div>
                                <h3 className="mt-5 font-display text-lg font-bold">{f.t}</h3>
                                <p className="mt-2 text-sm text-[#5C5C5C] leading-relaxed">{f.d}</p>
                                <span className="mt-6 inline-block font-mono text-xs text-[#5C5C5C]">0{idx+1}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* SEGMENTS */}
            <section id="pro" className="border-t border-[#E5E5E0]">
                <div className="mx-auto max-w-7xl px-6 py-20 grid md:grid-cols-2 gap-8">
                    <div className="hl-card p-8">
                        <span className="hl-pill">Per professionisti</span>
                        <h3 className="mt-4 font-display text-3xl font-bold tracking-tight">Trova collaborazioni sanitarie nella tua zona.</h3>
                        <p className="mt-3 text-[#5C5C5C]">Igienisti, infermieri, fisioterapisti, farmacisti, psicologi e molte altre professioni. Profilo gratuito.</p>
                        <Link to="/register?role=professional">
                            <Button className="mt-6 bg-[#1C3F39] hover:bg-[#2A5C53]" data-testid="segment-pro-cta">Crea il tuo profilo gratis</Button>
                        </Link>
                    </div>
                    <div id="facility" className="hl-card p-8 bg-[#1C3F39] text-white border-transparent">
                        <span className="hl-pill bg-white/10 text-white border-white/20">Per strutture</span>
                        <h3 className="mt-4 font-display text-3xl font-bold tracking-tight">Copri un turno in poche ore.</h3>
                        <p className="mt-3 text-white/80">Pubblica un turno, ricevi candidature qualificate ordinate per compatibilità.</p>
                        <Link to="/register?role=facility">
                            <Button className="mt-6 bg-[#D96C5B] hover:bg-[#c25a4a] text-white" data-testid="segment-facility-cta">Trova un professionista</Button>
                        </Link>
                    </div>
                </div>
            </section>

            <footer className="border-t border-[#E5E5E0] bg-white">
                <div className="mx-auto max-w-7xl px-6 py-10 text-sm text-[#5C5C5C] flex flex-wrap justify-between gap-4">
                    <span>© {new Date().getFullYear()} HealthLink — Marketplace sanitario</span>
                    <span className="font-mono text-xs">v1.0 · MVP</span>
                </div>
            </footer>
        </div>
    );
}
